import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Briefcase, ListBullets, GitDiff, CheckCircle, Clock, Hourglass, Warning, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchableSelect from '../components/SearchableSelect';
import { todayIst } from '../lib/datetime';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  done:        { l: 'Done',         icon: CheckCircle, bg: '#dcfce7', fg: '#15803d' },
  in_progress: { l: 'In Progress',  icon: Clock,       bg: '#dbeafe', fg: '#1d4ed8' },
  yet_to_start:{ l: 'Yet to Start', icon: Hourglass,   bg: '#f3e8ff', fg: '#6b21a8' },
  blocked:     { l: 'Blocked',      icon: Warning,     bg: '#fee2e2', fg: '#b91c1c' },
};

// Date helpers — all in IST local
const todayStr = todayIst;
const addDays = (yyyy_mm_dd, n) => {
  const [y, m, d] = yyyy_mm_dd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};
const startOfWeekISO = (yyyy_mm_dd) => {
  const [y, m, d] = yyyy_mm_dd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const dow = dt.getDay(); // 0 = Sun
  const diff = (dow === 0 ? -6 : 1 - dow); // Mon-start week
  dt.setDate(dt.getDate() + diff);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const startOfMonthISO = (yyyy_mm_dd) => `${yyyy_mm_dd.slice(0, 7)}-01`;

const RANGES = [
  { v: 'today',     l: 'Today' },
  { v: 'yesterday', l: 'Yesterday' },
  { v: 'week',      l: 'This Week' },
  { v: 'month',     l: 'This Month' },
  { v: 'compare',   l: 'Yesterday vs Today' },
  { v: 'custom',    l: 'Custom' },
];

export default function ClientWorkboardPage() {
  const today = todayStr();
  const [range, setRange] = useState('today');
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [empFilter, setEmpFilter] = useState('all');

  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ done: 0, in_progress: 0, yet_to_start: 0, blocked: 0 });
  // Compare-mode: paired items keyed by date
  const [compareItems, setCompareItems] = useState({ yesterday: [], today: [] });
  const [loading, setLoading] = useState(false);

  // Resolve date range from selected preset
  const applyRange = useCallback((r) => {
    const t = todayStr();
    if (r === 'today') { setFromDate(t); setToDate(t); }
    else if (r === 'yesterday') { const y = addDays(t, -1); setFromDate(y); setToDate(y); }
    else if (r === 'week') { setFromDate(startOfWeekISO(t)); setToDate(t); }
    else if (r === 'month') { setFromDate(startOfMonthISO(t)); setToDate(t); }
    else if (r === 'compare') { setFromDate(addDays(t, -1)); setToDate(t); }
    // 'custom' → user controls dates
  }, []);

  useEffect(() => {
    // Initial bootstrap data
    const cfg = { withCredentials: true };
    Promise.all([
      axios.get(`${API_URL}/api/clients`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/projects`, cfg).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/employees`, cfg).catch(() => ({ data: [] })),
    ]).then(([cl, pr, em]) => {
      setClients(cl.data || []);
      setProjects(pr.data || []);
      setEmployees(em.data || []);
    });
  }, []);

  useEffect(() => { applyRange(range); }, [range, applyRange]);

  const load = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (clientId) params.client_id = clientId;
      if (projectId) params.project_id = projectId;
      const r = await axios.get(`${API_URL}/api/client-workboard`, { params, withCredentials: true });
      setItems(r.data.items || []);
      setSummary(r.data.summary || { done: 0, in_progress: 0, yet_to_start: 0, blocked: 0 });
      if (range === 'compare') {
        const t = todayStr();
        const y = addDays(t, -1);
        const all = r.data.items || [];
        setCompareItems({
          yesterday: all.filter((it) => it.date === y),
          today: all.filter((it) => it.date === t),
        });
      }
    } catch (e) {
      toast.error('Load failed: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [fromDate, toDate, clientId, projectId, range]);

  useEffect(() => { load(); }, [load]);

  // Final filtered list (client-side: status + employee)
  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (statusFilter !== 'all' && it.status !== statusFilter) return false;
      if (empFilter !== 'all' && it.employee_id !== empFilter && it.user_id !== empFilter) return false;
      return true;
    });
  }, [items, statusFilter, empFilter]);

  // Group by date (newest first), employee within
  const grouped = useMemo(() => {
    const buckets = {};
    filtered.forEach((it) => {
      if (!buckets[it.date]) buckets[it.date] = [];
      buckets[it.date].push(it);
    });
    return Object.entries(buckets)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([date, rows]) => ({ date, rows }));
  }, [filtered]);

  const copyAll = () => {
    const lines = [];
    grouped.forEach(({ date, rows }) => {
      lines.push(`\n=== ${date} ===`);
      rows.forEach((it, i) => {
        const meta = `[${(STATUS_META[it.status] || STATUS_META.in_progress).l.toUpperCase()}]`;
        const who = it.employee_name ? `${it.employee_name}: ` : '';
        const ctx = [it.client_name && `🤝 ${it.client_name}`, it.project_name && `📁 ${it.project_name}`].filter(Boolean).join(' · ');
        lines.push(`${i + 1}. ${meta} ${who}${it.text}${ctx ? '  (' + ctx + ')' : ''}`);
      });
    });
    const text = lines.join('\n').trim();
    if (!text) { toast.error('Nothing to copy'); return; }
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`Copied ${filtered.length} task(s)`),
      () => toast.error('Copy failed'),
    );
  };

  const renderItemRow = (it, i) => {
    const sm = STATUS_META[it.status] || STATUS_META.in_progress;
    const SIcon = sm.icon;
    return (
      <li
        key={`${it.standup_id}-${it.item_index}-${i}`}
        className="flex items-start gap-3 py-2 px-3 border-b last:border-b-0"
        data-testid={`wb-item-${it.standup_id}-${it.item_index}`}
      >
        <span className="text-[11px] text-gray-400 font-mono w-5 mt-0.5">{i + 1}.</span>
        <SIcon size={16} weight="fill" style={{ color: sm.fg, marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <div className={`text-sm ${it.status === 'done' ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
            {it.text}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
              👤 {it.employee_name || '—'}
            </span>
            {it.client_name && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">
                🤝 {it.client_name}
              </span>
            )}
            {it.project_name && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-100">
                📁 {it.project_name}
              </span>
            )}
          </div>
        </div>
        <span className="text-[9px] font-bold px-1.5 py-1 rounded uppercase shrink-0"
              style={{ background: sm.bg, color: sm.fg }}>
          {sm.l}
        </span>
      </li>
    );
  };

  return (
    <div className="space-y-4" data-testid="client-workboard-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--dx-text1)' }}>
            <Briefcase size={26} weight="duotone" /> Client Workboard
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            All tasks across teams — filtered by client / project / date — perfect for daily stand-up reviews.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={copyAll} data-testid="wb-copy-all">
          <Copy size={14} className="mr-1.5" /> Copy All
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">Range</Label>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="w-full text-sm border rounded px-2 py-2 bg-white mt-1"
              data-testid="wb-range"
            >
              {RANGES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">From</Label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setRange('custom'); }}
              className="w-full text-sm border rounded px-2 py-2 bg-white mt-1"
              data-testid="wb-from"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">To</Label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setRange('custom'); }}
              className="w-full text-sm border rounded px-2 py-2 bg-white mt-1"
              data-testid="wb-to"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">Client</Label>
            <SearchableSelect
              testId="wb-client"
              value={clientId}
              onChange={setClientId}
              placeholder="All clients"
              options={clients.map((c) => {
                const fullName = `${c.first_name || ''} ${c.last_name || ''}`.trim();
                return {
                  value: c.id,
                  label: fullName || c.company_name || '(unnamed)',
                  sublabel: c.company_name && fullName ? c.company_name : (c.email || ''),
                  search: `${fullName} ${c.company_name || ''} ${c.email || ''}`,
                };
              })}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">Project</Label>
            <SearchableSelect
              testId="wb-project"
              value={projectId}
              onChange={setProjectId}
              placeholder="All projects"
              options={projects.map((p) => ({
                value: p.id,
                label: p.name,
                sublabel: p.status || '',
                search: `${p.name || ''} ${p.status || ''}`,
              }))}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">Status</Label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full text-sm border rounded px-2 py-2 bg-white mt-1"
              data-testid="wb-status"
            >
              <option value="all">All</option>
              <option value="done">✓ Done</option>
              <option value="in_progress">↻ In Progress</option>
              <option value="yet_to_start">⧗ Yet to Start</option>
              <option value="blocked">⛔ Blocked</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">Employee</Label>
            <SearchableSelect
              testId="wb-employee"
              value={empFilter === 'all' ? '' : empFilter}
              onChange={(v) => setEmpFilter(v || 'all')}
              placeholder="All employees"
              options={employees.map((e) => ({
                value: e.id,
                label: e.name,
                sublabel: e.designation_name || e.department_name || '',
                search: `${e.name} ${e.designation_name || ''} ${e.department_name || ''}`,
              }))}
            />
          </div>
          <div className="flex items-end">
            <div className="text-[11px] text-gray-500">
              {(clientId || projectId || empFilter !== 'all' || statusFilter !== 'all') && (
                <button
                  type="button"
                  onClick={() => { setClientId(''); setProjectId(''); setEmpFilter('all'); setStatusFilter('all'); }}
                  className="px-2 py-1.5 border rounded bg-white hover:bg-gray-50"
                  data-testid="wb-clear-filters"
                >Clear filters</button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Card className="p-3" data-testid="wb-stat-total">
          <div className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">Total</div>
          <div className="text-2xl font-bold text-slate-800">{filtered.length}</div>
        </Card>
        {(['done', 'in_progress', 'yet_to_start', 'blocked']).map((k) => {
          const m = STATUS_META[k];
          return (
            <Card key={k} className="p-3" data-testid={`wb-stat-${k}`}>
              <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: m.fg }}>
                {m.l}
              </div>
              <div className="text-2xl font-bold" style={{ color: m.fg }}>
                {filtered.filter((it) => it.status === k).length}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Compare mode: side-by-side Yesterday vs Today */}
      {range === 'compare' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wb-compare-view">
          {[
            { key: 'yesterday', label: 'Yesterday', accent: '#9333ea', date: addDays(today, -1) },
            { key: 'today',     label: 'Today',     accent: '#2563eb', date: today },
          ].map((col) => {
            // Compare mode uses fresh `compareItems` derived from items.
            // Re-apply client-side filters (status, emp) for parity with main list.
            const rows = (compareItems[col.key] || []).filter((it) => {
              if (statusFilter !== 'all' && it.status !== statusFilter) return false;
              if (empFilter !== 'all' && it.employee_id !== empFilter && it.user_id !== empFilter) return false;
              return true;
            });
            return (
              <Card key={col.key} className="overflow-hidden" data-testid={`wb-compare-${col.key}`}>
                <div className="px-3 py-2 border-b flex items-center justify-between" style={{ background: col.accent + '12' }}>
                  <div>
                    <div className="text-sm font-bold flex items-center gap-2" style={{ color: col.accent }}>
                      <GitDiff size={16} weight="duotone" /> {col.label}
                    </div>
                    <div className="text-[10px] text-gray-500">{col.date}</div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-1 rounded uppercase" style={{ background: col.accent, color: '#fff' }}>
                    {rows.length} task{rows.length === 1 ? '' : 's'}
                  </span>
                </div>
                {rows.length === 0 ? (
                  <div className="p-6 text-center text-xs text-gray-400">No tasks for this day</div>
                ) : (
                  <ul>
                    {rows.map((it, i) => renderItemRow(it, i))}
                  </ul>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
          ) : grouped.length === 0 ? (
            <Card className="p-8 text-center">
              <ListBullets size={36} className="mx-auto mb-2 text-gray-300" />
              <div className="text-sm text-gray-500">No standup tasks found for the selected filters.</div>
              <div className="text-[11px] text-gray-400 mt-1">Try widening the date range or clearing filters.</div>
            </Card>
          ) : grouped.map(({ date, rows }) => (
            <Card key={date} className="overflow-hidden" data-testid={`wb-day-${date}`}>
              <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between">
                <div className="text-sm font-bold text-slate-800">{date}</div>
                <span className="text-[10px] font-bold px-2 py-1 rounded bg-slate-200 text-slate-700">
                  {rows.length} task{rows.length === 1 ? '' : 's'}
                </span>
              </div>
              <ul>
                {rows.map((it, i) => renderItemRow(it, i))}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
